import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const UserRoleEnum = z.enum(['super_admin', 'manager', 'content_manager', 'viewer']);
export type UserRole = z.infer<typeof UserRoleEnum>;

export const ProductStatusEnum = z.enum(['draft', 'available', 'out_of_stock', 'discontinued']);
export type ProductStatus = z.infer<typeof ProductStatusEnum>;

export const NewsStatusEnum = z.enum(['draft', 'published', 'archived']);
export type NewsStatus = z.infer<typeof NewsStatusEnum>;

export const InquiryStatusEnum = z.enum(['new', 'contacted', 'quoted', 'closed', 'spam']);
export type InquiryStatus = z.infer<typeof InquiryStatusEnum>;

export const MessageStatusEnum = z.enum(['unread', 'read', 'replied', 'archived']);
export type MessageStatus = z.infer<typeof MessageStatusEnum>;

// ============================================
// CORE ENTITY SCHEMAS
// ============================================

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(100),
  role: UserRoleEnum,
  avatar_url: z.string().url().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  last_sign_in_at: z.string().datetime().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const ProductCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  slug: z.string().min(1).max(100),
  description: z.string().nullable().optional(),
  image_url: z.string().url().nullable().optional(),
  sort_order: z.number().int().default(0),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type ProductCategory = z.infer<typeof ProductCategorySchema>;

export const ProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  slug: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  short_description: z.string().max(500).nullable().optional(),
  category_id: z.string().uuid(),
  price: z.number().nonnegative().nullable().optional(),
  currency: z.enum(['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'DZD', 'MAD', 'XOF', 'GNF']).default('USD'),
  image_url: z.string().url().nullable().optional(),
  gallery_images: z.array(z.string().url()).default([]),
  features: z.array(z.string()).default([]),
  specifications: z.record(z.string(), z.string()).default({}),
  included_items: z.array(z.string()).default([]),
  status: ProductStatusEnum.default('draft'),
  featured: z.boolean().default(false),
  sort_order: z.number().int().default(0),
  meta_title: z.string().max(60).nullable().optional(),
  meta_description: z.string().max(160).nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  published_at: z.string().datetime().nullable().optional(),
});
export type Product = z.infer<typeof ProductSchema>;

export const ProductWithCategorySchema = ProductSchema.extend({
  category: ProductCategorySchema.nullable().optional(),
});
export type ProductWithCategory = z.infer<typeof ProductWithCategorySchema>;

export const NewsSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(200),
  content: z.string(),
  excerpt: z.string().max(500).nullable().optional(),
  category: z.enum(['company', 'industry', 'launches', 'events', 'tips']).default('company'),
  featured_image_url: z.string().url().nullable().optional(),
  gallery_images: z.array(z.string().url()).default([]),
  author_id: z.string().uuid(),
  status: NewsStatusEnum.default('draft'),
  featured: z.boolean().default(false),
  meta_title: z.string().max(60).nullable().optional(),
  meta_description: z.string().max(160).nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  published_at: z.string().datetime().nullable().optional(),
});
export type News = z.infer<typeof NewsSchema>;

export const NewsWithAuthorSchema = NewsSchema.extend({
  author: UserSchema.pick({ id: true, name: true, avatar_url: true }).nullable().optional(),
});
export type NewsWithAuthor = z.infer<typeof NewsWithAuthorSchema>;

export const GallerySchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  image_url: z.string().url(),
  thumbnail_url: z.string().url().nullable().optional(),
  category: z.string().max(50).nullable().optional(),
  sort_order: z.number().int().default(0),
  is_published: z.boolean().default(true),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Gallery = z.infer<typeof GallerySchema>;

export const PartnerSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  description: z.string().nullable().optional(),
  logo_url: z.string().url().nullable().optional(),
  website_url: z.string().url().nullable().optional(),
  type: z.enum(['international', 'national']),
  is_active: z.boolean().default(true),
  sort_order: z.number().int().default(0),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Partner = z.infer<typeof PartnerSchema>;

export const TeamMemberSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  role: z.string().min(1).max(100),
  bio: z.string().nullable().optional(),
  image_url: z.string().url().nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(30).nullable().optional(),
  conversation_id: z.string().uuid().nullable().optional(),
  chat_token: z.string().min(16).nullable().optional(),
  linkedin_url: z.string().url().nullable().optional(),
  sort_order: z.number().int().default(0),
  is_active: z.boolean().default(true),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const AboutContentSchema = z.object({
  id: z.string().default('main'),
  hero_description: z.string().default(''),
  company_paragraphs: z.array(z.string()).default([]),
  story_image_url: z.string().url().nullable().optional(),
  story_video_url: z.string().url().nullable().optional(),
  values_subtitle: z.string().default('The principles that guide everything we do.'),
  values: z.array(z.object({ title: z.string(), description: z.string() })).default([]),
  milestones_subtitle: z.string().default('Key milestones in our 15+ year history.'),
  milestones: z.array(z.object({ year: z.string(), title: z.string(), description: z.string() })).default([]),
  team_subtitle: z.string().default('Experienced professionals dedicated to your success.'),
  ecosystem_subtitle: z.string().default('From poultry and veterinary products to fresh produce, aquaculture, and trusted national and international partners.'),
  leadership_title: z.string().default('Meet Our Leadership'),
  ecosystem_title: z.string().default('Our products & ecosystem'),
  partners_title: z.string().default('Our Trusted Partners'),
  certifications_title: z.string().default('Certifications & Recognition'),
  certifications_subtitle: z.string().default('Industry-recognized excellence in service and sales'),
  certifications: z.array(z.object({ title: z.string(), subtitle: z.string(), icon: z.string() })).default([]),
  whatsapp_number: z.string().default(''),
  social_links: z.object({ facebook: z.string().default(''), twitter: z.string().default(''), linkedin: z.string().default(''), instagram: z.string().default('') }).default({ facebook: '', twitter: '', linkedin: '', instagram: '' }),
  updated_at: z.string().datetime().optional(),
});
export type AboutContent = z.infer<typeof AboutContentSchema>;

export const InquirySchema = z.object({
  id: z.string().uuid(),
  customer_name: z.string().min(1).max(100),
  phone: z.string().min(1).max(30),
  email: z.string().email().nullable().optional(),
  product_id: z.string().uuid().nullable().optional(),
  subject: z.string().max(200).nullable().optional(),
  message: z.string().min(1),
  status: InquiryStatusEnum.default('new'),
  assigned_to: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  contacted_at: z.string().datetime().nullable().optional(),
});
export type Inquiry = z.infer<typeof InquirySchema>;

export const InquiryWithProductSchema = InquirySchema.extend({
  product: ProductSchema.pick({ id: true, name: true, slug: true }).nullable().optional(),
  assignee: UserSchema.pick({ id: true, name: true, email: true }).nullable().optional(),
});
export type InquiryWithProduct = z.infer<typeof InquiryWithProductSchema>;

export const ContactMessageSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().max(30).nullable().optional(),
  subject: z.string().max(200).nullable().optional(),
  message: z.string().min(1),
  status: MessageStatusEnum.default('unread'),
  replied_at: z.string().datetime().nullable().optional(),
  replied_by: z.string().uuid().nullable().optional(),
  reply_message: z.string().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type ContactMessage = z.infer<typeof ContactMessageSchema>;

export const PublicFeedbackSchema = z.object({
  id: z.string().uuid(),
  content_type: z.enum(['product', 'news', 'service']),
  content_id: z.string(),
  author_name: z.string().min(1).max(100),
  author_email: z.string().email(),
  rating: z.number().int().min(1).max(5).nullable().optional(),
  comment: z.string().min(3).max(2000),
  status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type PublicFeedback = z.infer<typeof PublicFeedbackSchema>;

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid().nullable().optional(), // null = broadcast to all admins
  type: z.enum(['inquiry', 'message', 'product', 'news', 'system']),
  title: z.string().min(1).max(200),
  message: z.string(),
  reference_id: z.string().uuid().nullable().optional(), // e.g., inquiry_id
  reference_type: z.string().nullable().optional(),
  is_read: z.boolean().default(false),
  created_at: z.string().datetime(),
  read_at: z.string().datetime().nullable().optional(),
});
export type Notification = z.infer<typeof NotificationSchema>;

// ============================================
// INPUT SCHEMAS (for forms/API)
// ============================================

export const CreateProductSchema = ProductSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  published_at: true,
});
export type CreateProductInput = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = CreateProductSchema.partial();
export type UpdateProductInput = z.infer<typeof UpdateProductSchema>;

export const CreateNewsSchema = NewsSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  published_at: true,
  author_id: true,
});
export type CreateNewsInput = z.infer<typeof CreateNewsSchema>;

export const UpdateNewsSchema = CreateNewsSchema.partial();
export type UpdateNewsInput = z.infer<typeof UpdateNewsSchema>;

export const CreateGallerySchema = GallerySchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type CreateGalleryInput = z.infer<typeof CreateGallerySchema>;

export const CreatePartnerSchema = PartnerSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type CreatePartnerInput = z.infer<typeof CreatePartnerSchema>;

export const CreateTeamMemberSchema = TeamMemberSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type CreateTeamMemberInput = z.infer<typeof CreateTeamMemberSchema>;

export const CreateInquirySchema = InquirySchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  contacted_at: true,
  status: true,
  assigned_to: true,
  notes: true,
});
export type CreateInquiryInput = z.infer<typeof CreateInquirySchema>;

export const CreateContactMessageSchema = ContactMessageSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  status: true,
  replied_at: true,
  replied_by: true,
  reply_message: true,
});
export type CreateContactMessageInput = z.infer<typeof CreateContactMessageSchema>;

export const CreateNotificationSchema = NotificationSchema.omit({
  id: true,
  created_at: true,
  read_at: true,
});
export type CreateNotificationInput = z.infer<typeof CreateNotificationSchema>;

export const UpdateInquiryStatusSchema = z.object({
  status: InquiryStatusEnum,
  notes: z.string().nullable().optional(),
  assigned_to: z.string().uuid().nullable().optional(),
});
export type UpdateInquiryStatusInput = z.infer<typeof UpdateInquiryStatusSchema>;

export const ReplyContactMessageSchema = z.object({
  reply_message: z.string().min(1),
});
export type ReplyContactMessageInput = z.infer<typeof ReplyContactMessageSchema>;

// ============================================
// QUERY/FILTER SCHEMAS
// ============================================

export const PaginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sort_by: z.string().optional(),
  sort_order: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationParams = z.infer<typeof PaginationSchema>;

export const ProductFilterSchema = PaginationSchema.extend({
  category_id: z.string().uuid().optional(),
  status: ProductStatusEnum.optional(),
  featured: z.boolean().optional(),
  search: z.string().optional(),
});
export type ProductFilterParams = z.infer<typeof ProductFilterSchema>;

export const NewsFilterSchema = PaginationSchema.extend({
  status: NewsStatusEnum.optional(),
  featured: z.boolean().optional(),
  search: z.string().optional(),
  author_id: z.string().uuid().optional(),
});
export type NewsFilterParams = z.infer<typeof NewsFilterSchema>;

export const InquiryFilterSchema = PaginationSchema.extend({
  status: InquiryStatusEnum.optional(),
  product_id: z.string().uuid().optional(),
  assigned_to: z.string().uuid().optional(),
  search: z.string().optional(),
  date_from: z.string().datetime().optional(),
  date_to: z.string().datetime().optional(),
});
export type InquiryFilterParams = z.infer<typeof InquiryFilterSchema>;

// ============================================
// API RESPONSE WRAPPERS
// ============================================

export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.nullable().optional(),
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.record(z.unknown()).optional(),
    }).nullable().optional(),
    meta: z.object({
      page: z.number().optional(),
      limit: z.number().optional(),
      total: z.number().optional(),
      total_pages: z.number().optional(),
    }).optional(),
  });

export type ApiResponse<T> = {
  success: boolean;
  data: T | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta?: { page?: number; limit?: number; total?: number; total_pages?: number };
};

// ============================================
// SUPABASE DATABASE TYPES (generated)
// ============================================

// These will be populated by `supabase gen types`
// See packages/shared/src/database.types.ts after running db:generate
export type Database = {
  public: {
    Tables: {
      users: { 
        Row: User; 
        Insert: Omit<User, 'id' | 'created_at' | 'updated_at'>; 
        Update: Omit<Partial<User>, 'id' | 'created_at' | 'updated_at'> 
      };
      product_categories: { 
        Row: ProductCategory; 
        Insert: Omit<ProductCategory, 'id' | 'created_at' | 'updated_at'>; 
        Update: Omit<Partial<ProductCategory>, 'id' | 'created_at' | 'updated_at'> 
      };
      products: { 
        Row: Product;
        Insert: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'published_at'>;
        Update: Omit<Partial<Product>, 'id' | 'created_at' | 'updated_at' | 'published_at'>
      };
      news: { 
        Row: News; 
        Insert: Omit<News, 'id' | 'created_at' | 'updated_at' | 'published_at'>; 
        Update: Omit<Partial<News>, 'id' | 'created_at' | 'updated_at' | 'published_at'> 
      };
      gallery: { 
        Row: Gallery; 
        Insert: Omit<Gallery, 'id' | 'created_at' | 'updated_at'>; 
        Update: Omit<Partial<Gallery>, 'id' | 'created_at' | 'updated_at'> 
      };
      partners: { 
        Row: Partner; 
        Insert: Omit<Partner, 'id' | 'created_at' | 'updated_at'>; 
        Update: Omit<Partial<Partner>, 'id' | 'created_at' | 'updated_at'> 
      };
      team_members: { 
        Row: TeamMember; 
        Insert: Omit<TeamMember, 'id' | 'created_at' | 'updated_at'>; 
        Update: Omit<Partial<TeamMember>, 'id' | 'created_at' | 'updated_at'> 
      };
      about_content: {
        Row: AboutContent;
        Insert: Omit<AboutContent, 'updated_at'>;
        Update: Partial<Omit<AboutContent, 'id' | 'updated_at'>>;
      };
      inquiries: { 
        Row: Inquiry; 
        Insert: Omit<Inquiry, 'id' | 'created_at' | 'updated_at' | 'contacted_at'>; 
        Update: Omit<Partial<Inquiry>, 'id' | 'created_at' | 'updated_at' | 'contacted_at'> 
      };
      contact_messages: { 
        Row: ContactMessage; 
        Insert: Omit<ContactMessage, 'id' | 'created_at' | 'updated_at' | 'replied_at'>; 
        Update: Omit<Partial<ContactMessage>, 'id' | 'created_at' | 'updated_at' | 'replied_at'> 
      };
      public_feedback: {
        Row: PublicFeedback;
        Insert: Omit<PublicFeedback, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<PublicFeedback, 'id' | 'created_at' | 'updated_at'>>;
      };
      notifications: { 
        Row: Notification; 
        Insert: Omit<Notification, 'id' | 'created_at' | 'read_at'>; 
        Update: Omit<Partial<Notification>, 'id' | 'created_at'> 
      };
    };
    Views: {};
    Functions: {};
    Enums: {
      user_role: UserRole;
      product_status: ProductStatus;
      news_status: NewsStatus;
      inquiry_status: InquiryStatus;
      message_status: MessageStatus;
    };
  };
};

export * from './repositories';
export * from './sync';
export * from './supabaseSync';
